import { createContext, ReactNode, useRef, useState, useMemo, useContext } from "react"

interface Handlers<StatusType> {
    push: (status: StatusType) => void
    goBack: () => void
    onPush: (cb: (status: StatusType) => void) => void
}

export function createStatusContext<StatusType>(initialStatus?: StatusType) {
    const defaultHandlers: Handlers<StatusType> = {
        push: () => undefined,
        goBack: () => undefined,
        onPush: () => undefined,
    }

    const StatusContext = createContext<[StatusType | undefined, Handlers<StatusType>]>([
        undefined,
        defaultHandlers,
    ])
    function StatusProvider({ children }: { children: ReactNode }) {
        const callbacks = useRef<((status: StatusType) => void)[]>([])
        const [statuses, setStatuses] = useState<StatusType[]>(initialStatus ? [initialStatus] : [])
        const handlers = useMemo(
            (): Handlers<StatusType> => ({
                push: (status) => {
                    setStatuses((statuses) => statuses.concat(status))
                    for (const fn of callbacks.current) {
                        fn(status)
                    }
                },
                goBack: () => {
                    setStatuses(statuses.slice(0, statuses.length - 1))
                },
                onPush: (cb) => {
                    callbacks.current.push(cb)
                },
            }),
            [statuses]
        )
        const currentStatus = statuses[statuses.length - 1]

        return (
            <StatusContext.Provider value={[currentStatus, handlers]}>
                {children}
            </StatusContext.Provider>
        )
    }
    return {
        StatusContext,
        StatusProvider,
        useStatusContext: () => useContext(StatusContext),
    }
}
